import { hiddenItemIds } from "./conditions.ts";
import { isFieldItem } from "./items.ts";
import type { FormField, FormItem } from "./types.ts";

export type FormStep<F extends FormField = FormField> = {
  section: Extract<FormItem<F>, { kind: "section" }> | null;
  items: FormItem<F>[];
};

export function formSteps<F extends FormField>(form: { items: readonly FormItem<F>[] }): FormStep<F>[] {
  const steps: FormStep<F>[] = [];
  let current: FormStep<F> | null = null;
  for (const item of form.items) {
    if (item.kind === "section") {
      current = { items: [item], section: item };
      steps.push(current);
      continue;
    }
    if (!current) {
      current = { items: [], section: null };
      steps.push(current);
    }
    current.items.push(item);
  }
  return steps.length === 0 ? [{ items: [], section: null }] : steps;
}

/** A text-only page is not blank. */
export function isBlankStep<F extends FormField>(step: FormStep<F>, hidden: ReadonlySet<string>): boolean {
  const questions = step.items.filter(isFieldItem);
  return questions.length > 0 && questions.every((item) => hidden.has(item.itemId));
}

export function nextAskedStep<F extends FormField>(
  steps: readonly FormStep<F>[],
  index: number,
  hidden: ReadonlySet<string>,
): number | "submit" {
  for (let next = index + 1; next < steps.length; next += 1) {
    if (!isBlankStep(steps[next]!, hidden)) return next;
  }
  return "submit";
}

export type FormWalk<F extends FormField = FormField> = {
  path: number[];
  items: FormItem<F>[];
  hidden: Set<string>;
};

export function walkForm<F extends FormField>(
  form: { items: readonly FormItem<F>[] },
  answers: Record<string, unknown>,
  catalog: ReadonlyMap<string, F>,
  timeZone?: string,
): FormWalk<F> {
  const steps = formSteps(form);
  const hidden = hiddenItemIds(form.items, answers, catalog, timeZone);
  const path: number[] = [];
  const items: FormItem<F>[] = [];
  steps.forEach((step, index) => {
    if (isBlankStep(step, hidden)) return;
    path.push(index);
    items.push(...step.items.filter((item) => !hidden.has(item.itemId)));
  });
  return { hidden, items, path };
}

/** A held-off question leaves nothing behind. */
export function answersOnPath<F extends FormField>(
  answers: Record<string, unknown>,
  items: readonly FormItem<F>[],
): Record<string, unknown> {
  const kept: Record<string, unknown> = {};
  for (const item of items) {
    if (isFieldItem(item) && item.itemId in answers) kept[item.itemId] = answers[item.itemId];
  }
  return kept;
}
